import { ArrowRight, Play, ThumbsUp, DollarSign, Sparkles, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SectionHeader from "@/components/SectionHeader";
const services = [
  {
    title: "Testimonials",
    description: "Authentische Kundenstimmen, die Vertrauen schaffen und überzeugen.",
    scrollTarget: "work",
    variant: "dark" as const,
    visualType: "testimonial" as const
  },
  {
    title: "Imagefilme",
    description: "Cinematische Markenfilme mit emotionaler Tiefe.",
    scrollTarget: "work",
    variant: "light" as const,
    visualType: "film" as const
  },
  {
    title: "Webseiten",
    description: "Moderne Webseiten, die konvertieren.",
    linkTo: "/webseiten",
    variant: "dark" as const,
    visualType: "website" as const
  },
  {
    title: "KI-Visuals",
    description: "Hochwertige KI-generierte Visuals in Rekordzeit.",
    scrollTarget: "ki-productions",
    variant: "accent" as const,
    visualType: "ai" as const
  }
];

// Testimonial Visual - People speaking in front of camera with engagement
const TestimonialVisual = () => (
  <div className="relative w-full h-52 flex items-start justify-center px-4 pt-2">
    <div className="relative w-full max-w-[200px]">
      {/* Camera viewfinder frame */}
      <div className="w-full h-32 bg-gradient-to-b from-foreground/10 to-foreground/5 rounded-xl border-2 border-foreground/30 overflow-hidden relative">
        {/* Corner brackets */}
        <div className="absolute top-2 left-2 w-4 h-4 border-l-2 border-t-2 border-accent/60" />
        <div className="absolute top-2 right-2 w-4 h-4 border-r-2 border-t-2 border-accent/60" />
        <div className="absolute bottom-2 left-2 w-4 h-4 border-l-2 border-b-2 border-accent/60" />
        <div className="absolute bottom-2 right-2 w-4 h-4 border-r-2 border-b-2 border-accent/60" />
        
        {/* Three people silhouettes */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex items-end justify-center gap-3">
          {/* Left person */}
          <div className="relative">
            <div className="w-10 h-16 bg-foreground/20 rounded-t-full" />
            <motion.div 
              className="absolute -top-2 -right-1"
              animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1] }}
              transition={{ duration: 0.5, delay: 0.8, repeat: Infinity, repeatDelay: 3 }}
            >
              <ThumbsUp className="w-4 h-4 text-accent" />
            </motion.div>
          </div>
          
          {/* Center person (main speaker) */}
          <div className="relative">
            <div className="w-12 h-20 bg-foreground/25 rounded-t-full" />
            {/* Speech waves */}
            <motion.div 
              className="absolute top-2 -right-4 flex flex-col gap-0.5"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1, repeat: Infinity }}
            >
              <div className="w-3 h-0.5 bg-accent/60 rounded-full" />
              <div className="w-4 h-0.5 bg-accent/80 rounded-full" />
              <div className="w-2 h-0.5 bg-accent/50 rounded-full" />
            </motion.div>
          </div>
          
          {/* Right person */}
          <div className="relative">
            <div className="w-10 h-16 bg-foreground/20 rounded-t-full" />
            <motion.div 
              className="absolute -top-2 -left-1"
              animate={{ scale: [0, 1.2, 1], opacity: [0, 1, 1] }}
              transition={{ duration: 0.5, delay: 1.5, repeat: Infinity, repeatDelay: 3 }}
            >
              <ThumbsUp className="w-4 h-4 text-accent" />
            </motion.div>
          </div>
        </div>
        
        {/* REC indicator */}
        <motion.div 
          className="absolute top-2 left-2 flex items-center gap-1.5"
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-[9px] text-red-400 font-bold">REC</span>
        </motion.div>
      </div>
      
      {/* Floating thumbs up reactions */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: `${20 + i * 25}%`, bottom: 35 }}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: [-20, -60], opacity: [0, 1, 0] }}
          transition={{ duration: 1.2, delay: 2 + i * 0.3, repeat: Infinity, repeatDelay: 2 }}
        >
          <ThumbsUp className="w-5 h-5 text-accent/80" />
        </motion.div>
      ))}
    </div>
  </div>
);

// Film Visual - Video preview with play and dollar signs going up
const FilmVisual = () => (
  <div className="relative w-full h-52 flex items-start justify-center px-4 pt-2">
    <div className="relative w-full max-w-[200px]">
      {/* Video preview frame */}
      <div className="w-full h-32 bg-gradient-to-br from-background/80 to-background/40 rounded-xl border border-background/30 overflow-hidden relative">
        {/* Video content placeholder */}
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-transparent to-accent/5" />
        
        {/* Play button */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center"
        >
          <motion.div 
            className="w-14 h-14 rounded-full bg-background/90 border border-background/50 flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.1 }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Play className="w-6 h-6 text-foreground ml-1" fill="currentColor" />
          </motion.div>
        </motion.div>

        {/* Mouse cursor clicking */}
        <motion.div 
          className="absolute"
          initial={{ x: 20, y: 80 }}
          animate={{ 
            x: [20, 85, 85, 85],
            y: [80, 55, 55, 55],
          }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        >
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none" className="drop-shadow-lg">
            <path d="M0 0L0 16L4 12L8 20L10 19L6 11L12 11L0 0Z" fill="white" stroke="black" strokeWidth="1"/>
          </svg>
        </motion.div>

        {/* Dollar signs floating up after click */}
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-accent font-bold"
            style={{ left: `${30 + i * 15}%` }}
            initial={{ y: 100, opacity: 0 }}
            animate={{ 
              y: [100, 20, -20],
              opacity: [0, 1, 0]
            }}
            transition={{ 
              duration: 1.5, 
              delay: 2.2 + i * 0.15, 
              repeat: Infinity, 
              repeatDelay: 1.5 
            }}
          >
            <DollarSign className="w-4 h-4" />
          </motion.div>
        ))}
      </div>

      {/* Timeline bar */}
      <div className="absolute -bottom-2 left-0 right-0 h-1.5 bg-background/30 rounded-full overflow-hidden">
        <motion.div 
          className="h-full bg-accent/80 rounded-full"
          animate={{ width: ["0%", "70%"] }}
          transition={{ duration: 2, delay: 2, repeat: Infinity, repeatDelay: 1 }}
        />
      </div>
    </div>
  </div>
);

// Website Visual - Browser with revenue chart
const WebsiteVisual = () => (
  <div className="relative w-full h-52 flex items-start justify-center px-4 pt-2">
    <div className="w-full max-w-[200px]">
      {/* Browser window */}
      <div className="w-full h-36 bg-foreground/5 rounded-xl border border-foreground/15 overflow-hidden">
        {/* Browser header */}
        <div className="h-5 bg-foreground/10 border-b border-foreground/10 flex items-center px-2 gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
          <div className="ml-2 flex-1 h-2 bg-foreground/10 rounded" />
        </div>
        
        {/* Website content */}
        <div className="p-2 space-y-2">
          {/* Hero section */}
          <div className="w-full h-6 bg-accent/20 rounded flex items-center justify-center">
            <div className="w-8 h-2 bg-accent/40 rounded" />
          </div>
          
          {/* Stats row with animated chart */}
          <div className="flex gap-1.5 h-16">
            {/* Mini chart going up */}
            <div className="flex-1 bg-foreground/5 rounded p-1.5 flex items-end gap-0.5">
              {[30, 45, 35, 60, 50, 75, 90].map((height, i) => (
                <motion.div
                  key={i}
                  className="flex-1 bg-accent/60 rounded-t"
                  initial={{ height: 0 }}
                  animate={{ height: `${height}%` }}
                  transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity, repeatDelay: 3 }}
                />
              ))}
            </div>
            
            {/* Revenue indicator */}
            <div className="w-12 bg-accent/10 rounded p-1 flex flex-col items-center justify-center">
              <TrendingUp className="w-3 h-3 text-accent mb-0.5" />
              <motion.div 
                className="flex items-center"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <DollarSign className="w-2.5 h-2.5 text-accent" />
                <span className="text-[8px] text-accent font-bold">+127%</span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// AI Visual - Morphing abstract shapes with particles
const AIVisual = () => (
  <div className="relative w-full h-52 flex items-start justify-center px-4 pt-4 overflow-hidden">
    {/* Central morphing shape */}
    <div className="relative">
      <motion.div
        className="w-24 h-24 rounded-3xl bg-gradient-to-br from-accent via-accent/60 to-accent/20"
        animate={{
          borderRadius: ["30%", "50%", "40%", "60%", "30%"],
          rotate: [0, 90, 180, 270, 360],
          scale: [1, 1.1, 0.9, 1.05, 1]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          boxShadow: "0 0 60px hsl(158 73% 42% / 0.5), 0 0 120px hsl(158 73% 42% / 0.2)"
        }}
      />
      
      {/* Inner glow */}
      <motion.div
        className="absolute inset-2 rounded-2xl bg-accent/40 blur-sm"
        animate={{
          borderRadius: ["40%", "50%", "30%", "45%", "40%"],
          opacity: [0.4, 0.7, 0.5, 0.8, 0.4]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Sparkle icon */}
      <motion.div 
        className="absolute inset-0 flex items-center justify-center"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
      >
        <Sparkles className="w-8 h-8 text-background/80" />
      </motion.div>
    </div>

    {/* Floating particles */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute w-2 h-2 rounded-full bg-accent/60"
        style={{
          left: `${20 + Math.random() * 60}%`,
          top: `${20 + Math.random() * 60}%`,
        }}
        animate={{
          y: [0, -20, 0],
          x: [0, Math.random() * 20 - 10, 0],
          opacity: [0.3, 1, 0.3],
          scale: [0.5, 1, 0.5]
        }}
        transition={{
          duration: 2 + Math.random() * 2,
          delay: i * 0.3,
          repeat: Infinity
        }}
      />
    ))}

    {/* Orbiting dots */}
    {[...Array(3)].map((_, i) => (
      <motion.div
        key={`orbit-${i}`}
        className="absolute w-3 h-3 rounded-full bg-accent"
        style={{
          boxShadow: "0 0 10px hsl(158 73% 42% / 0.8)"
        }}
        animate={{
          rotate: 360
        }}
        transition={{
          duration: 3 + i,
          repeat: Infinity,
          ease: "linear"
        }}
        initial={{
          x: Math.cos((i * 120 * Math.PI) / 180) * (50 + i * 10),
          y: Math.sin((i * 120 * Math.PI) / 180) * (50 + i * 10)
        }}
      />
    ))}
  </div>
);

const getVisual = (type: string) => {
  switch (type) {
    case "testimonial":
      return <TestimonialVisual />;
    case "film":
      return <FilmVisual />;
    case "website":
      return <WebsiteVisual />;
    case "ai":
      return <AIVisual />;
    default:
      return null;
  }
};

const Services = () => {
  const navigate = useNavigate();
  
  const handleClick = (service: typeof services[0]) => {
    if (service.linkTo) {
      navigate(service.linkTo);
    } else if (service.scrollTarget) {
      document.getElementById(service.scrollTarget)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 md:py-32 bg-background relative">
      <div className="container mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Was wir machen"
          title="Unsere Leistungen"
          highlightedWord="Leistungen"
          className="mb-16"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => handleClick(service)}
              className={`group relative flex flex-col rounded-3xl cursor-pointer transition-all duration-500 overflow-hidden min-h-[380px] ${
                service.variant === "light" 
                  ? "bg-foreground text-background hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.2)]" 
                  : service.variant === "accent"
                    ? "bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/30 hover:border-accent/50 hover:shadow-[0_20px_60px_-15px_hsl(158_73%_42%_/_0.3)]"
                    : "bg-card border border-border hover:border-foreground/20 hover:shadow-[0_20px_60px_-15px_rgba(255,255,255,0.1)]"
              }`}
            >
              {/* Content */}
              <div className="p-6 flex-1 flex flex-col">
                <h3 className={`text-2xl md:text-3xl font-normal tracking-tight mb-3 ${
                  service.variant === "light" ? "text-background" : "text-foreground"
                }`}>
                  {service.title}
                </h3>
                
                <p className={`text-sm leading-relaxed ${
                  service.variant === "light" ? "text-background/70" : "text-muted-foreground"
                }`}>
                  {service.description}
                </p>
                
                {/* Arrow */}
                <div className={`mt-4 inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 group-hover:gap-3 ${
                  service.variant === "light" ? "text-background/80" : service.variant === "accent" ? "text-accent" : "text-foreground/60"
                }`}>
                  <span>Mehr erfahren</span>
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>

              {/* Visual at bottom */}
              <div className={`mt-auto ${service.variant === "light" ? "text-background" : ""}`}>
                {getVisual(service.visualType)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;